import api from './api'

/** 货架产品 id → 后台 opengate_models.model_id */
const PRODUCT_PRICING_MAP = {
  'gpt-image': ['gpt-image-2', 'gpt-image-2.5-flare', 'gpt-image-2.5-sunburst'],
  'banana-image': ['nano-banana', 'nano-banana-2'],
  'seedance-2': ['doubao-seedance-2-0-260128', 'doubao-seedance-2-0-fast-260128'],
  'kling-image-gen': ['kling-image'],
  'kling-multi-ref': ['kling-multi-image'],
  'kling-outpaint': ['kling-outpaint'],
  'kling-omni': ['kling-omni'],
  'kling-motion': ['kling-motion-control'],
  'kling-digital-human': ['kling-digital-human'],
  'kling-mm-edit': ['kling-mm-edit'],
  midjourney: ['midjourney'],
  'suno-relink': ['suno-relink'],
  'suno-midi': ['suno-midi'],
  'sora-veo-grok': null // 按前缀匹配
}

/** @type {{ byModel: Record<string, object>, byPath: Record<string, object>, list: array, loadedAt: number } | null} */
let cache = null

/**
 * 解开 Dingo / corsJson 多层 { code, data } 包装，直到拿到含 pricing/list 的业务体
 */
function unwrapProductsPayload(res) {
  let cur = res
  for (let i = 0; i < 8; i++) {
    if (!cur || typeof cur !== 'object') return null
    if (Array.isArray(cur.pricing) || Array.isArray(cur.list) || cur.pricing_by_model) {
      return cur
    }
    if (cur.data != null && typeof cur.data === 'object' && !Array.isArray(cur.data)) {
      cur = cur.data
      continue
    }
    break
  }
  return null
}

/**
 * 拉取开放平台定价（GET /idoing_products，公开）
 * @param {{ force?: boolean }} [opts]
 */
export async function loadOpenGatePricing(opts = {}) {
  if (!opts.force && cache && Date.now() - cache.loadedAt < 60000) {
    return cache
  }
  const res = await api.get('/idoing_products')
  const data = unwrapProductsPayload(res)
  if (!data) {
    const preview = typeof res === 'object' ? JSON.stringify(res).slice(0, 200) : String(res)
    throw new Error('定价响应结构无法解析：' + preview)
  }

  const byModel = {}
  const pricing = Array.isArray(data.pricing) ? data.pricing : []
  const pricingByModel =
    data.pricing_by_model && typeof data.pricing_by_model === 'object' ? data.pricing_by_model : null
  if (pricingByModel) {
    Object.keys(pricingByModel).forEach(k => {
      byModel[k] = pricingByModel[k]
    })
  } else {
    pricing.forEach(row => {
      if (row && row.model_id) byModel[row.model_id] = row
    })
  }

  if (!Object.keys(byModel).length) {
    throw new Error('后台暂无上架的 opengate_models 定价')
  }

  const byPath = {}
  const list = Array.isArray(data.list) ? data.list : []
  list.forEach(item => {
    if (!item || !item.path) return
    byPath[item.path] = item
  })

  cache = { byModel, byPath, list, loadedAt: Date.now() }
  return cache
}

export function clearOpenGatePricingCache() {
  cache = null
}

function formatRowsLabel(rows) {
  if (!rows || !rows.length) return null
  const credits = rows.map(r => Number(r.credits) || 0)
  const min = Math.min(...credits)
  const max = Math.max(...credits)
  let unit = String(rows[0].price_unit || '次').trim() || '次'
  for (let i = 1; i < rows.length; i++) {
    const u = String(rows[i].price_unit || '').trim()
    if (u && u !== unit) {
      unit = '次'
      break
    }
  }
  if (max <= 0) return { text: '免费', paid: false }
  if (min === max) return { text: `${min}积分/${unit}`, paid: true }
  return { text: `${min}~${max}积分/${unit}`, paid: true }
}

/**
 * 用后台定价覆盖货架产品价格文案
 * @param {object} product apiCatalog 产品
 * @param {Awaited<ReturnType<typeof loadOpenGatePricing>> | null} pricing
 */
export function applyPricingToProduct(product, pricing) {
  if (!product) return product
  const fallback = { ...product }

  // 任务查询类：不另计费
  if (product.createEndpointId || String(product.path || '').includes('{task_id}')) {
    return { ...product, priceLabel: '不另计费', paid: false }
  }
  if (product.id === 'idoing-ping' || product.id === 'idoing-products' || product.id === 'idoing-task-show') {
    return { ...product, priceLabel: '免费', paid: false }
  }

  if (!pricing || !pricing.byModel) return fallback

  // 优先按产品映射的 model_id
  const mapped = PRODUCT_PRICING_MAP[product.id]
  if (mapped === null && product.id === 'sora-veo-grok') {
    const rows = Object.keys(pricing.byModel || {})
      .filter(id => /^(sora-|veo|grok-video)/i.test(id))
      .map(id => pricing.byModel[id])
    const label = formatRowsLabel(rows)
    if (label) return { ...product, priceLabel: label.text, paid: label.paid }
  }
  if (Array.isArray(mapped) && mapped.length) {
    const rows = mapped.map(id => pricing.byModel[id]).filter(Boolean)
    const label = formatRowsLabel(rows)
    if (label) return { ...product, priceLabel: label.text, paid: label.paid }
  }

  // 回退：按 path 命中 /idoing_products.list[].price
  const item = pricing.byPath[product.path]
  if (item && item.price && item.price.label) {
    if (product.id === 'banana-image' && product.path === '/idoing_gpt_image') {
      return fallback
    }
    return {
      ...product,
      priceLabel: item.price.label,
      paid: !!item.price.paid
    }
  }

  return fallback
}
